import { put, takeLatest, takeEvery, call, fork, spawn, take, select, delay } from 'redux-saga/effects'
import MessageSocket from '../../../services/socket/MessageSocket'
import ListMetaData from '../../../modules/utilities/list/ListMetaData'
import ErrorActions from '../../actions/Error'
import OLError from '../../../modules/errorHandling/models/OLError'
import CHAT_MANAGEMENT_EXPERIENCE_TYPES from '../../types/experiences/ChatManagement'
import AUTH_EXPERIENCE_TYPES from '../../types/experiences/Auth'
import { messageShouldUpdateOpenChatList } from '../../../modules/chat/chatThreadMessages'
import { notifyIncomingMessage, invalidateMessageSoundCache } from '../../../modules/notifications/messageNotifications'
import { socketAckErrorToError } from '../../../modules/chat/socketApiErrors'
import { resetTitleFlash } from '../../../modules/notifications/messageTitleFlash'
import { selectMainChatUnreadBadge } from '../../../modules/chat/selectMainChatUnreadBadge'
import LayoutActions from '../../actions/Layout'
import MessageDataStoreSlice from '../../slices/data/MessageDataStoreSlice'
import ChatManagementMessageListSlice from '../../slices/experiences/ChatManagementMessageListSlice'
import ChatManagementExperienceActions from '../../actions/experiences/ChatManagement'
import MessageActions from '../../actions/Messages'
import { AUTO_HIDE_MESSAGE_AFTER_SEC_DEFAULT, DISPLAYED_MESSAGE_TYPES, IS_DEV_ENV } from '../../../constants/general'
import StoreMessageCategory from '../../models/MessageCategory'
import MessageCategoryService from '../../../services/MessageCategory'
import MessageCategoryDataStoreSlice from '../../slices/data/MessageCategoryDataStoreSlice'
import ChatManagementChatGroupListSlice from '../../slices/experiences/ChatManagementChatGroupListSlice'
import ChatGroupSocket from '../../../services/socket/ChatGroupSocket'
import ChatGroupDataStoreSlice from '../../slices/data/ChatGroupDataStoreSlice'
import { selectAC } from '../../selectors/AC'
import { hasPermissionKey } from '../../../modules/ac/permissionKeys'
import { eventChannel } from 'redux-saga'
import StoreMessage from '../../models/Message'
import {
  normalizeMessagesCreatedPayload,
  normalizeChatGroupMessageCreatedPayload
} from '../../../modules/chat/socketMessageNormalization'
import { buildSocketMessageCreatePayload } from '../../../modules/chat/messageSocketCreatePayload'
import { resolveDeviceChatIds } from '../../../modules/chat/resolveDeviceChatIds'
import {
  normalizeChatGroupListSocketResult,
  normalizeChatGroupSocketRecord
} from '../../../modules/chat/normalizeChatGroupListSocketResult'
import { isChatGroupActive } from '../../../modules/chat/isChatGroupActive'
import { getDataStoreEntityMap } from '../../../modules/chat/dataStoreEntityMap'
import { isDangerMessage } from '../../../modules/chat/isDangerMessage'
function isGroupSocketPayload (payload) {
  if (!payload || typeof payload !== 'object') return false
  const raw = payload.message || payload.data?.message || payload
  return (
    payload.receiverChatGroupId != null ||
    payload.chatGroupId != null ||
    payload.receiver_chat_group_id != null ||
    raw?.receiverChatGroupId != null ||
    raw?.chatGroupId != null ||
    raw?.receiver_chat_group_id != null ||
    raw?.chat_group_id != null
  )
}

function normalizeIncomingSocketMessage (payload, messageCategoriesById) {
  if (isGroupSocketPayload(payload)) {
    return (
      normalizeChatGroupMessageCreatedPayload(payload, messageCategoriesById) ||
      normalizeMessagesCreatedPayload(payload)
    )
  }
  return (
    normalizeMessagesCreatedPayload(payload) ||
    normalizeChatGroupMessageCreatedPayload(payload, messageCategoriesById)
  )
}
import { isIncomingOperatorMessage } from '../../../modules/chat/isIncomingOperatorMessage'
import { shouldNotifyIncomingMessage } from '../../../modules/chat/shouldNotifyIncomingMessage'
import { buildChatGroupUnreadClearPatches } from '../../../modules/chat/clearChatGroupUnreadInStore'
import {
  buildAllMainChatInboxUnreadClearPatches,
  buildMainChatThreadUnreadClearPatches,
  collectUnreadMainChatDeviceUuids
} from '../../../modules/chat/clearMainChatThreadUnreadInStore'
import { CHAT_DRAWER_PANEL } from '../../../routes/Admin/components/ChatContainer/constants'
import LAYOUT_TYPES from '../../types/Layout'
import SortParameter from '../../../modules/utilities/list/SortParameter'
import {
  resolveChatGroupMemberDeviceIds,
  resolveChatGroupMemberUserIds
} from '../../../modules/chat/chatGroupMemberOptions'

const CHAT_MESSAGE_LIST_MAX_ITEMS = 2000

function buildChatGroupSocketUpdatePayload (existing, overrides = {}) {
  return {
    name: existing.name,
    description: overrides.description !== undefined
      ? overrides.description
      : (existing.description ?? null),
    photoId: overrides.photoId !== undefined ? overrides.photoId : (existing.photoId ?? null),
    userIds: overrides.userIds ?? resolveChatGroupMemberUserIds(existing),
    deviceIds: overrides.deviceIds ?? resolveChatGroupMemberDeviceIds(existing)
  }
}

function isChatGroupMutationSuccess (result) {
  if (!result) return false
  if (result.status === 200) return true
  return result.id != null && String(result.id).length > 0
}

function * resolveDeviceUuidForRead (deviceId) {
  if (!deviceId) return null
  const devicesById = getDataStoreEntityMap(yield select(state => state.data.devices))
  return resolveDeviceChatIds(devicesById, deviceId).uuid || deviceId
}

function * applyMainChatThreadUnreadClear ({ deviceUuid, userId }) {
  const profileId = yield select(state => state.experiences.auth.profile?.id)
  const rootState = yield select(state => state)
  const messagePatches = buildMainChatThreadUnreadClearPatches(rootState, {
    deviceUuid: deviceUuid || null,
    userId: userId || null,
    profileId
  })
  for (const { id, patch } of messagePatches) {
    yield put(MessageDataStoreSlice.actions.addItem(id, patch))
  }
  return messagePatches.length
}

/** Mark every unread main-chat inbox row read (aggregate list / drawer open — legacy global clear). */
function * markAllMainChatInboxRead () {
  const profileId = yield select(state => state.experiences.auth.profile?.id)
  if (!profileId) return

  const activeContext = yield select(state => state.experiences.chatManagement.activeContext)
  if (activeContext?.deviceId || activeContext?.userId || activeContext?.chatGroupId) {
    return
  }

  const rootState = yield select(state => state)
  const deviceUuids = collectUnreadMainChatDeviceUuids(rootState, profileId)
  const patches = buildAllMainChatInboxUnreadClearPatches(rootState, profileId)
  for (const { id, patch } of patches) {
    yield put(MessageDataStoreSlice.actions.addItem(id, patch))
  }
  for (const deviceUuid of deviceUuids) {
    yield call(MessageSocket.markThreadRead, deviceUuid)
  }

  const badge = selectMainChatUnreadBadge(yield select(state => state))
  if (badge.count <= 0) {
    resetTitleFlash()
  }
}

/** Legacy parity: operator opened/read a device or user thread — clear local unreads and notify socket. */
function * markActiveThreadRead () {
  const activeContext = yield select(state => state.experiences.chatManagement.activeContext)
  if (!activeContext?.subscribed) return

  if (activeContext.deviceId) {
    const deviceUuidForRead = yield * resolveDeviceUuidForRead(activeContext.deviceId)
    yield * applyMainChatThreadUnreadClear({ deviceUuid: deviceUuidForRead, userId: null })
    if (deviceUuidForRead) {
      yield call(MessageSocket.markThreadRead, deviceUuidForRead)
    }
  } else if (activeContext.userId) {
    yield * applyMainChatThreadUnreadClear({ deviceUuid: null, userId: activeContext.userId })
  } else if (activeContext.chatGroupId) {
    yield * applyChatGroupUnreadClear(activeContext.chatGroupId)
    yield call(MessageSocket.markChatGroupRead, activeContext.chatGroupId)
  } else {
    yield * markAllMainChatInboxRead()
  }

  const badge = selectMainChatUnreadBadge(yield select(state => state))
  if (badge.count <= 0) {
    resetTitleFlash()
  }
}

function * markAllChatGroupsInboxRead () {
  const activeContext = yield select(state => state.experiences.chatManagement.activeContext)
  if (activeContext?.chatGroupId) return

  const chatGroupsById = getDataStoreEntityMap(yield select(state => state.data.chatGroups))
  for (const group of Object.values(chatGroupsById)) {
    if (!isChatGroupActive(group)) continue
    const unread = Number(group.nrOfUnreadMessages) || 0
    if (unread <= 0) continue
    const groupId = String(group.id)
    yield * applyChatGroupUnreadClear(groupId)
    yield call(MessageSocket.markChatGroupRead, groupId)
  }
}

function * onChatDrawerMaybeOpened () {
  const layout = yield select(state => state.ui.layout)
  if (!layout.chatDrawerOpen) return
  if (layout.chatDrawerPanel === CHAT_DRAWER_PANEL.MAIN) {
    yield * markAllMainChatInboxRead()
  } else if (layout.chatDrawerPanel === CHAT_DRAWER_PANEL.GROUPS) {
    yield * markAllChatGroupsInboxRead()
  }
}

function * applyChatGroupUnreadClear (chatGroupId) {
  if (!chatGroupId) return
  const profileId = yield select(state => state.experiences.auth.profile?.id)
  const rootState = yield select(state => state)
  const { chatGroupPatch, messagePatches } = buildChatGroupUnreadClearPatches(
    rootState,
    String(chatGroupId),
    profileId
  )
  if (chatGroupPatch) {
    yield put(ChatGroupDataStoreSlice.actions.addItem(chatGroupPatch.id, chatGroupPatch.item))
  }
  for (const { id, patch } of messagePatches) {
    yield put(MessageDataStoreSlice.actions.addItem(id, patch))
  }
}

function * patchChatGroupOnIncomingMessage (message, profileId, messageCategoriesById) {
  if (!message?.receiverChatGroupId) return

  const chatGroupsById = getDataStoreEntityMap(yield select(state => state.data.chatGroups))
  const groupId = String(message.receiverChatGroupId)
  const existing = chatGroupsById[groupId]
  if (!existing) {
    yield spawn(scheduleChatGroupUnreadRefresh)
    return
  }

  const isIncomingToOperator =
    !!profileId &&
    message.senderUserId !== profileId &&
    isIncomingOperatorMessage(message, profileId)

  const activeContext = yield select(state => state.experiences.chatManagement.activeContext)
  const chatDrawerOpen = yield select(state => state.ui.layout.chatDrawerOpen)
  const chatDrawerPanel = yield select(state => state.ui.layout.chatDrawerPanel)
  const isOpenGroupThread =
    chatDrawerOpen &&
    chatDrawerPanel === 'groups' &&
    activeContext?.subscribed &&
    String(activeContext?.chatGroupId || '') === groupId

  const createdAt = message.createdAt || message.timestamp || new Date().toISOString()
  const shouldIncrementUnread = isIncomingToOperator && !isOpenGroupThread

  yield put(
    ChatGroupDataStoreSlice.actions.addItem(groupId, {
      ...existing,
      lastMessageAt: createdAt,
      lastMessageContent: message.content ?? existing.lastMessageContent,
      nrOfUnreadMessages:
        (Number(existing.nrOfUnreadMessages) || 0) + (shouldIncrementUnread ? 1 : 0),
      hasDangerUnread:
        !!existing.hasDangerUnread ||
        (isIncomingToOperator && isDangerMessage(message, messageCategoriesById))
    })
  )
}

function * applyChatGroupToStore (socketResult) {
  const record = normalizeChatGroupSocketRecord(socketResult)
  if (!record?.id) {
    const rawId = socketResult?.id ?? socketResult?.chatGroupId
    if (rawId != null && !isChatGroupActive(socketResult)) {
      yield * removeChatGroupFromExperience(String(rawId))
    }
    return
  }
  const chatGroupsById = getDataStoreEntityMap(yield select(state => state.data.chatGroups))
  const existing = chatGroupsById[String(record.id)]
  const userIds = record.userIds ?? existing?.userIds ?? []
  const deviceIds = record.deviceIds ?? existing?.deviceIds ?? []

  const merged = {
    ...(existing || {}),
    ...record,
    userIds,
    deviceIds,
    memberCount: userIds.length,
    attachedDeviceCount: deviceIds.length,
    users: record.users ?? [],
    devices: record.devices ?? [],
    nrOfUnreadMessages: record.nrOfUnreadMessages ?? existing?.nrOfUnreadMessages ?? 0,
    hasDangerUnread: record.hasDangerUnread ?? existing?.hasDangerUnread ?? false,
    lastMessageAt: record.lastMessageAt ?? existing?.lastMessageAt ?? null,
    lastMessageContent: record.lastMessageContent ?? existing?.lastMessageContent ?? null,
    createdByUserId: record.createdByUserId ?? existing?.createdByUserId ?? null
  }

  yield put(ChatGroupDataStoreSlice.actions.addItem(String(record.id), merged))
}

function * removeChatGroupFromExperience (chatGroupId) {
  if (!chatGroupId) return
  yield put(ChatGroupDataStoreSlice.actions.deleteItem(chatGroupId))
  const chatGroupList = yield select(state => state.experiences.chatManagement?.chatGroupList)
  const currentIds = Array.isArray(chatGroupList?.data) ? chatGroupList.data : []
  if (currentIds.includes(chatGroupId)) {
    const nextIds = currentIds.filter(id => id !== chatGroupId)
    yield put(
      ChatManagementExperienceActions.chatGroupList.setData(
        nextIds,
        Math.max(0, (chatGroupList?.totalNumberOfItems || 0) - 1),
        false
      )
    )
  }
  const activeContext = yield select(state => state.experiences.chatManagement?.activeContext)
  if (activeContext?.chatGroupId === chatGroupId) {
    yield put(
      ChatManagementExperienceActions.unsubscribeContext({
        chatGroupId,
        userId: activeContext.userId,
        deviceId: activeContext.deviceId
      })
    )
  }
}

function normalizeChatGroupPushPayload (payload) {
  if (Array.isArray(payload) && payload.length > 1) return payload[1]
  return payload
}

function assertMessageListResponse (result) {
  if (result?.error != null) {
    throw socketAckErrorToError(result.error)
  }
}

function * fetchMessages (action) {
  const { data: { limit, page, sort, filters } } = action
  const isInitialPage = !page
  try {
    if (isInitialPage) {
      yield put(ChatManagementExperienceActions.messageList.setIsLoading(true))
    }


    const offset = page * limit
    const result = yield MessageSocket.FindMany(
      new ListMetaData(
        limit,
        offset,
        sort,
        filters
      )
    )
    assertMessageListResponse(result)

    yield put(
      MessageDataStoreSlice.actions.fetchResult(
        result,
        function * (storeValues) {
          const currentList = yield select(state => state.experiences.chatManagement.messageList?.data)
          const currentIds = Array.isArray(currentList) ? currentList : []
          const newIds = storeValues.result || []
          const isAppend = page > 0
          const nextIds = isAppend
            ? [...currentIds, ...newIds].slice(0, CHAT_MESSAGE_LIST_MAX_ITEMS)
            : newIds.slice(0, CHAT_MESSAGE_LIST_MAX_ITEMS)
          yield put(
            ChatManagementExperienceActions.messageList.setData(nextIds, storeValues.totalNrOfItems, false)
          )
          if (isInitialPage) {
            yield * markActiveThreadRead()
          }
        }
      )
    )
  } catch (e) {
    yield put(
      ErrorActions.trigger(
        new OLError('[ChatManagementExperience->fetchMessages]: Failed to fetch messages', 'Failed to fetch messages', e),
        true,
        true,
        true,
        false
      )
    )
  }
  if (isInitialPage) {
    yield put(ChatManagementExperienceActions.messageList.setIsLoading(false))
  }
}

function * fetchChatGroupMessages (action) {
  const { data: { chatGroupId, deviceUuid, limit, page, sort } } = action
  const isInitialPage = !page
  try {
    if (isInitialPage) {
      yield put(ChatManagementExperienceActions.messageList.setIsLoading(true))
    }


    const offset = page * limit
    const result = yield MessageSocket.FindManyForChatGroup(
      chatGroupId,
      deviceUuid,
      new ListMetaData(limit, offset, sort, null)
    )

    yield put(
      MessageDataStoreSlice.actions.fetchResult(
        result,
        function * (storeValues) {
          const currentList = yield select(state => state.experiences.chatManagement.messageList?.data)
          const currentIds = Array.isArray(currentList) ? currentList : []
          const newIds = storeValues.result || []
          const isAppend = page > 0
          const nextIds = isAppend
            ? [...currentIds, ...newIds].slice(0, CHAT_MESSAGE_LIST_MAX_ITEMS)
            : newIds.slice(0, CHAT_MESSAGE_LIST_MAX_ITEMS)
          yield put(
            ChatManagementExperienceActions.messageList.setData(nextIds, storeValues.totalNrOfItems, false)
          )
          if (isInitialPage) {
            yield * markActiveThreadRead()
          }
        }
      )
    )
  } catch (e) {
    yield put(
      ErrorActions.trigger(
        new OLError(
          '[ChatManagementExperience->fetchChatGroupMessages]: Failed to fetch chat group messages',
          'Failed to fetch chat group messages',
          e
        ),
        true,
        true,
        true,
        false
      )
    )
  }
  if (isInitialPage) {
    yield put(ChatManagementExperienceActions.messageList.setIsLoading(false))
  }
}

function * sendMessageToServer ({ messageItem, onSuccess }) {
  try {
    const isChatGroupMessage = !!messageItem.receiverChatGroupId
    const messageCategoriesById = getDataStoreEntityMap(yield select(state => state.data.messageCategories))
    const normalizedCategoryName = String(
      messageCategoriesById?.[messageItem.categoryId]?.name || ''
    ).toLowerCase()
    const result = isChatGroupMessage
      ? yield (function * () {
        const payload = {
          receiverChatGroupId: messageItem.receiverChatGroupId,
          content: messageItem.content,
          categoryName: ['info', 'warning', 'danger'].includes(normalizedCategoryName)
            ? normalizedCategoryName
            : 'info',
          categoryId: messageItem.categoryId
        }
        try {
          return yield MessageSocket.CreateForChatGroup(payload)
        } catch (err) {
          const fallbackPayload = {
            receiverChatGroupId: payload.receiverChatGroupId,
            content: payload.content,
            categoryName: payload.categoryName
          }
          return yield MessageSocket.CreateForChatGroup(fallbackPayload)
        }
      })()
      : messageItem.receiverDeviceId
        ? yield (function * () {
          const devicesById = yield select(state => state.data.devices?.data || {})
          const { storeId, uuid: receiverUuid } = resolveDeviceChatIds(
            devicesById,
            messageItem.receiverDeviceId
          )
          if (!receiverUuid) {
            throw new Error('Could not resolve the device for this conversation.')
          }
          const targetDevice = storeId ? devicesById[storeId] : null
          return yield MessageSocket.Create(
            buildSocketMessageCreatePayload(
              { ...messageItem, receiverDeviceId: receiverUuid },
              targetDevice,
              { categoryName: normalizedCategoryName }
            )
          )
        })()
        : yield MessageSocket.Create(
          buildSocketMessageCreatePayload(messageItem, null, {
            categoryName: normalizedCategoryName
          })
        )
    ;(result?.status === 200 || !!result) && onSuccess && onSuccess()
  } catch (e) {
    const displayedMessage = e?.message || 'Failed to create message'
    yield put(
      ErrorActions.trigger(
        new OLError(
          '[ChatManagementExperienceSaga->createMessage]: Failed to create message',
          displayedMessage,
          e
        ),
        true,
        true,
        true,
        false
      )
    )
    const messageList = yield select(state => state.experiences.chatManagement.messageList)
    const currentIds = Array.isArray(messageList?.data) ? messageList.data : []
    const filteredIds = currentIds.filter(id => !String(id).startsWith('temp-'))
    if (filteredIds.length !== currentIds.length) {
      yield put(
        ChatManagementExperienceActions.messageList.setData(
          filteredIds,
          Math.max(0, (messageList?.totalNumberOfItems || 0) - 1),
          false
        )
      )
    }
  }
}

function * createMessage (action) {
  try {
    const { data: { messageItem, onSuccess } } = action

    // optimistic UI: add a temporary message into store and list
    const tempId = `temp-${Date.now()}`
    const now = new Date()
    const profile = yield select(state => state.experiences.auth.profile)
    const tempMessage = {
      id: tempId,
      content: messageItem.content,
      type: messageItem.type,
      categoryId: messageItem.categoryId,
      senderUserId: profile?.id,
      senderDeviceId: 'operator',
      receiverDeviceId: messageItem.receiverDeviceId,
      receiverUserId: messageItem.receiverUserId,
      receiverChatGroupId: messageItem.receiverChatGroupId,
      createdAt: now,
      received: false,
      read: false
    }
    const activeContext = yield select(state => state.experiences.chatManagement.activeContext)
    const devicesById = getDataStoreEntityMap(yield select(state => state.data.devices))
    const shouldShowInList = messageShouldUpdateOpenChatList(
      tempMessage,
      activeContext,
      profile?.id,
      devicesById
    )
    yield put(MessageDataStoreSlice.actions.addItem(tempId, tempMessage))
    if (shouldShowInList) {
      const messageList = yield select(state => state.experiences.chatManagement.messageList)
      const currentIds = Array.isArray(messageList?.data) ? messageList.data : []
      const nextIds = [tempId, ...currentIds]
      yield put(ChatManagementExperienceActions.messageList.setData(nextIds, (messageList?.totalNumberOfItems || 0) + 1, false))
    }

    yield spawn(sendMessageToServer, { messageItem, onSuccess })
  } catch (e) {
    yield put(
      ErrorActions.trigger(
        new OLError('[ChatManagementExperienceSaga->createMessage]: Failed to queue message', 'Failed to send message', e),
        true,
        true,
        true,
        false
      )
    )
  }
}

function * editMessageCategory (action) {
  const fetchId = 'ChatManagementExperienceSaga.editMessageCategory'
  try {
    yield put(LayoutActions.setIsLoading(true, fetchId))

    const { data: { messageCategoryItem, onSuccess } } = action

    const result = yield MessageCategoryService.Update(
      messageCategoryItem.id,
      messageCategoryItem.toNormalizedRESTModel()
    )

    if (result.status === 200) {
      const updated = StoreMessageCategory.FromNormalizedRESTModel(result.data)
      if (messageCategoryItem?.soundPath) {
        invalidateMessageSoundCache(messageCategoryItem.soundPath)
      }
      if (updated.soundPath) {
        invalidateMessageSoundCache(updated.soundPath)
      }
      yield put(MessageCategoryDataStoreSlice.actions.addItem(String(updated.id), updated))
      yield put(
        MessageActions.add(
          DISPLAYED_MESSAGE_TYPES.SUCCESS,
          undefined,
          ['EDIT_MESSAGE_CATEGORY_SUCCESS', 'Message category successfully updated'],
          AUTO_HIDE_MESSAGE_AFTER_SEC_DEFAULT
        )
      )
      onSuccess && onSuccess()
    }
  } catch (e) {
    yield put(
      ErrorActions.trigger(
        new OLError(
          '[ChatManagementExperienceSaga->editMessageCategory]: Failed to update message category',
          'Failed to update message category',
          e
        ),
        true,
        true,
        true,
        false
      )
    )
  }
  yield put(LayoutActions.setIsLoading(false, fetchId))
}

function * fetchChatGroups (action) {
  try {
    yield put(ChatManagementExperienceActions.chatGroupList.setIsLoading(true))

    const { data: { limit, page, sort, filters } } = action

    const result = yield ChatGroupSocket.FindMany(
      new ListMetaData(
        limit,
        page,
        sort,
        filters
      )
    )

    yield put(
      ChatGroupDataStoreSlice.actions.fetchResult(
        result,
        function * (storeValues) {
          const chatGroupsById = getDataStoreEntityMap(yield select(state => state.data.chatGroups))
          const activeIds = (storeValues.result || []).filter(id =>
            isChatGroupActive(chatGroupsById[String(id)])
          )
          yield put(
            ChatManagementExperienceActions.chatGroupList.setData(activeIds, activeIds.length, false)
          )
        }
      )
    )
  } catch (e) {
    yield put(
      ErrorActions.trigger(
        new OLError(
          '[ChatManagementExperience->fetchChatGroups]: Failed to fetch chat groups',
          'Failed to fetch chat groups',
          e
        ),
        true,
        true,
        true,
        false
      )
    )
  }
  yield put(ChatManagementExperienceActions.chatGroupList.setIsLoading(false))
}

function * createChatGroup (action) {
  const fetchId = 'ChatManagementExperienceSaga.createChatGroup'
  try {
    yield put(LayoutActions.setIsLoading(true, fetchId))

    const { data: { chatGroupItem, onSuccess } } = action

    const result = yield ChatGroupSocket.Create(chatGroupItem.toNormalizedRESTModel())
    if (isChatGroupMutationSuccess(result)) {
      yield put(
        MessageActions.add(
          DISPLAYED_MESSAGE_TYPES.SUCCESS,
          undefined,
          ['CREATE_CHAT_GROUP_SUCCESS', 'Chat group successfully created'],
          AUTO_HIDE_MESSAGE_AFTER_SEC_DEFAULT
        )
      )
      yield * applyChatGroupToStore(result)
      onSuccess && onSuccess(result)
    }
  } catch (e) {
    yield put(
      ErrorActions.trigger(
        new OLError(
          '[ChatManagementExperience->createChatGroup]: Failed to create chat group',
          'Failed to create chat group',
          e
        ),
        true,
        true,
        true,
        false
      )
    )
  }
  yield put(LayoutActions.setIsLoading(false, fetchId))
}

function * editChatGroup (action) {
  const fetchId = 'ChatManagementExperienceSaga.editChatGroup'
  try {
    yield put(LayoutActions.setIsLoading(true, fetchId))

    const { data: { chatGroupItem, onSuccess } } = action

    const result = yield ChatGroupSocket.Update(chatGroupItem.id, chatGroupItem.toNormalizedRESTModel())
    if (isChatGroupMutationSuccess(result)) {
      yield put(
        MessageActions.add(
          DISPLAYED_MESSAGE_TYPES.SUCCESS,
          undefined,
          ['EDIT_CHAT_GROUP_SUCCESS', 'Chat group successfully updated'],
          AUTO_HIDE_MESSAGE_AFTER_SEC_DEFAULT
        )
      )
      yield * applyChatGroupToStore(result)
      onSuccess && onSuccess()
    }
  } catch (e) {
    yield put(
      ErrorActions.trigger(
        new OLError(
          '[ChatManagementExperience->editChatGroup]: Failed to update chat group',
          'Failed to update chat group',
          e
        ),
        true,
        true,
        true,
        false
      )
    )
  }
  yield put(LayoutActions.setIsLoading(false, fetchId))
}

function * deleteChatGroup (action) {
  const fetchId = 'ChatManagementExperienceSaga.deleteChatGroup'
  try {
    yield put(LayoutActions.setIsLoading(true, fetchId))

    const { data: { chatGroupId } } = action

    const result = yield ChatGroupSocket.Delete(chatGroupId)
    if (isChatGroupMutationSuccess(result) || result?.deleted) {
      yield put(
        MessageActions.add(
          DISPLAYED_MESSAGE_TYPES.SUCCESS,
          undefined,
          ['DELETE_CHAT_GROUP_SUCCESS', 'Chat group deleted'],
          AUTO_HIDE_MESSAGE_AFTER_SEC_DEFAULT
        )
      )
      yield put(ChatGroupDataStoreSlice.actions.deleteItem(chatGroupId))
    }
  } catch (e) {
    yield put(
      ErrorActions.trigger(
        new OLError(
          '[ChatGroupManagementExperience->deleteChatGroup]: Failed to delete chat group',
          'Failed to delete chat group',
          e
        ),
        true,
        true,
        true,
        false
      )
    )
  }
  yield put(LayoutActions.setIsLoading(false, fetchId))
}

function * patchChatGroupPhoto (action) {
  const { data: { chatGroupId, photoId } } = action
  try {
    const chatGroupsById = getDataStoreEntityMap(yield select(state => state.data.chatGroups))
    const existing = chatGroupsById[String(chatGroupId)]
    if (!existing) return

    const result = yield ChatGroupSocket.Update(
      chatGroupId,
      buildChatGroupSocketUpdatePayload(existing, { photoId })
    )
    if (isChatGroupMutationSuccess(result)) {
      yield * applyChatGroupToStore(result)
      yield put(
        MessageActions.add(
          DISPLAYED_MESSAGE_TYPES.SUCCESS,
          undefined,
          ['EDIT_CHAT_GROUP_PHOTO_SUCCESS', 'Group photo updated'],
          AUTO_HIDE_MESSAGE_AFTER_SEC_DEFAULT
        )
      )
    }
  } catch (e) {
    yield put(
      ErrorActions.trigger(
        new OLError(
          '[ChatManagementExperience->patchChatGroupPhoto]: Failed to update group photo',
          'Failed to update group photo',
          e
        ),
        true,
        true,
        true,
        false
      )
    )
  }
}

function * updateChatGroupMembers (action) {
  const { data: { chatGroupId, userIds, deviceIds } } = action
  const chatGroupsById = getDataStoreEntityMap(yield select(state => state.data.chatGroups))
  const existing = chatGroupsById[String(chatGroupId)]
  if (!existing) return

  const optimisticItem = {
    ...existing,
    userIds,
    deviceIds,
    memberCount: userIds.length,
    attachedDeviceCount: deviceIds.length,
    users: [],
    devices: []
  }
  yield put(ChatGroupDataStoreSlice.actions.addItem(String(chatGroupId), optimisticItem))

  try {
    const result = yield ChatGroupSocket.Update(
      chatGroupId,
      buildChatGroupSocketUpdatePayload(existing, { userIds, deviceIds })
    )
    if (isChatGroupMutationSuccess(result)) {
      yield * applyChatGroupToStore(result)
      yield put(
        MessageActions.add(
          DISPLAYED_MESSAGE_TYPES.SUCCESS,
          undefined,
          ['EDIT_CHAT_GROUP_MEMBERS_SUCCESS', 'Chat group membership updated'],
          AUTO_HIDE_MESSAGE_AFTER_SEC_DEFAULT
        )
      )
    } else {
      yield put(ChatGroupDataStoreSlice.actions.addItem(String(chatGroupId), existing))
    }
  } catch (e) {
    yield put(ChatGroupDataStoreSlice.actions.addItem(String(chatGroupId), existing))
    yield put(
      ErrorActions.trigger(
        new OLError(
          '[ChatManagementExperience->updateChatGroupMembers]: Failed to update chat group membership',
          'Failed to update chat group membership',
          e
        ),
        true,
        true,
        true,
        false
      )
    )
  }
}

function * updateChatGroupMemberAssignation (action) {
  const { data: { chatGroupId, memberKind, memberKey, assigned } } = action
  const chatGroupsById = getDataStoreEntityMap(yield select(state => state.data.chatGroups))
  const existing = chatGroupsById[String(chatGroupId)]
  if (!existing?.name) {
    yield put(
      ErrorActions.trigger(
        new OLError(
          '[ChatManagementExperience->updateChatGroupMemberAssignation]: Chat group not loaded',
          'Chat group is not loaded yet. Please wait and try again.',
          new Error('missing_chat_group')
        ),
        true,
        true,
        true,
        false
      )
    )
    return
  }

  let userIds = [...resolveChatGroupMemberUserIds(existing)]
  let deviceIds = [...resolveChatGroupMemberDeviceIds(existing)]
  const key = String(memberKey)

  if (memberKind === 'agent') {
    userIds = assigned
      ? [...new Set([...userIds, key])]
      : userIds.filter(id => String(id) !== key)
  } else if (memberKind === 'tracker') {
    deviceIds = assigned
      ? [...new Set([...deviceIds, key])]
      : deviceIds.filter(id => String(id) !== key)
  } else {
    return
  }

  const optimisticItem = {
    ...existing,
    userIds,
    deviceIds,
    memberCount: userIds.length,
    attachedDeviceCount: deviceIds.length,
    users: [],
    devices: []
  }

  yield put(ChatGroupDataStoreSlice.actions.addItem(String(chatGroupId), optimisticItem))

  try {
    const result = yield ChatGroupSocket.Update(
      chatGroupId,
      buildChatGroupSocketUpdatePayload(existing, { userIds, deviceIds })
    )
    if (isChatGroupMutationSuccess(result)) {
      yield * applyChatGroupToStore(result)
    } else {
      yield put(ChatGroupDataStoreSlice.actions.addItem(String(chatGroupId), existing))
    }
  } catch (e) {
    yield put(ChatGroupDataStoreSlice.actions.addItem(String(chatGroupId), existing))
    yield put(
      ErrorActions.trigger(
        new OLError(
          '[ChatManagementExperience->updateChatGroupMemberAssignation]: Failed to update chat group membership',
          'Failed to update chat group membership',
          e
        ),
        true,
        true,
        true,
        false
      )
    )
  }
}

function * subscribeAllChatGroupsForNotifications () {
  try {
    yield call(MessageSocket.ensureRealtimeChat)
    for (let attempt = 0; attempt < 12; attempt++) {
      const chatGroupsById = getDataStoreEntityMap(yield select(state => state.data.chatGroups))
      const groupIds = Object.keys(chatGroupsById)
      if (groupIds.length > 0) {
        yield call(MessageSocket.subscribeAllChatGroupsForNotifications, groupIds)
        return
      }
      yield delay(250)
    }
  } catch (e) {
    if (IS_DEV_ENV) {
      console.warn(
        '[ChatManagement] Chat group notification subscribe failed; group alerts may be delayed until a thread is opened.',
        e?.message || e
      )
    }
  }
}

function * refreshUnreadSources () {
  const isAuthenticated = yield select(state => state.experiences.auth.isAuthenticated)
  if (!isAuthenticated) return

  const { permissionKeys } = yield select(selectAC)
  if (hasPermissionKey(permissionKeys, 'messages', 'find')) {
    yield put(MessageCategoryDataStoreSlice.actions.fetch(null))
  }
  yield put(ChatGroupDataStoreSlice.actions.fetch({ ids: null }))
  yield spawn(subscribeAllChatGroupsForNotifications)
  yield put(
    ChatManagementExperienceActions.messageList.fetch(
      150,
      0,
      [SortParameter.DESC('createdAt')],
      null
    )
  )
}

function * scheduleChatGroupUnreadRefresh () {
  yield delay(400)
  yield put(ChatGroupDataStoreSlice.actions.fetch({ ids: null }))
}

function * watchAuthForSocket () {
  while (true) {
    const isAuthenticated = yield select(state => state.experiences.auth.isAuthenticated)
    if (isAuthenticated) {
      try {
        yield call(MessageSocket.ensureRealtimeChat)
        yield call(refreshUnreadSources)
      } catch (e) {
        if (IS_DEV_ENV) {
          console.warn(
            '[ChatManagement] Real-time chat setup failed (socket); REST and admin CRUD continue.',
            e?.message || e
          )
        }
      }
    }
    yield take(AUTH_EXPERIENCE_TYPES.SET_IS_AUTHENTICATED)
  }
}

function * maintainRealtimeChatSession () {
  yield fork(watchAuthForSocket)
}

function * ChatManagementExperienceSaga () {
  yield fork(maintainRealtimeChatSession)

  // start realtime listener once
  yield fork(function * watchNewMessages () {
    const channel = eventChannel(emit => {
      MessageSocket.onNewMessage(payload => emit(payload))
      return () => {}
    })

    while (true) {
      let payload = yield take(channel)
      // Socket may send array [ "messages created", payload ] or just the payload object
      if (Array.isArray(payload) && payload.length > 1) payload = payload[1]
      // Support three payload shapes:
      // 1) "messages created" socket event: { id_message, body_message, type_message, device_data, ... }
      // 2) "chatGroupMessage created" socket event: { message: {...} } or {...}
      // 3) Legacy: { data: { message: {...} } } or the message itself with .id
      const messageCategoriesById = getDataStoreEntityMap(yield select(state => state.data.messageCategories))
      const message =
        normalizeIncomingSocketMessage(payload, messageCategoriesById) ||
        payload?.data?.message ||
        payload?.message ||
        payload
      if (message?.id) {
        const profile = yield select(state => state.experiences.auth.profile)
        const activeContext = yield select(state => state.experiences.chatManagement.activeContext)
        const devicesById = getDataStoreEntityMap(yield select(state => state.data.devices))
        const hasOpenThread = !!activeContext?.subscribed

        if (!message.type) message.type = StoreMessage.SUPPORTED_MESSAGE_TYPES.TEXT
        yield put(MessageDataStoreSlice.actions.addItem(message.id, message))

        if (message.receiverChatGroupId) {
          yield spawn(patchChatGroupOnIncomingMessage, message, profile?.id, messageCategoriesById)
        }

        // Legacy parity: auto-ack delivery when a device message arrives so mobile sees ✓✓.
        const isIncomingToOperator = isIncomingOperatorMessage(message, profile?.id)
        const isIncomingDeviceMessage =
          isIncomingToOperator &&
          !!message.senderDeviceId &&
          !message.receiverChatGroupId
        if (isIncomingDeviceMessage) {
          const deviceUuidForReceived =
            payload?.uuid_device_target ||
            message.receiverDeviceId ||
            message.senderDeviceId
          if (deviceUuidForReceived) {
            yield spawn(MessageSocket.markMessageReceived, message.id, String(deviceUuidForReceived))
          }
        }

        // Notify (sound + browser notification) for incoming device / group messages on any thread.
        if (shouldNotifyIncomingMessage(message, profile?.id)) {
          const devicesByIdForNotify = getDataStoreEntityMap(yield select(state => state.data.devices))
          const chatGroupsById = getDataStoreEntityMap(yield select(state => state.data.chatGroups))
          const categoriesForNotify = getDataStoreEntityMap(yield select(state => state.data.messageCategories))
          const groupName = message.receiverChatGroupId
            ? chatGroupsById[message.receiverChatGroupId]?.name
            : undefined
          const senderName = message.senderDeviceId
            ? Object.values(devicesByIdForNotify).find(device => device?.uuidDevice === message.senderDeviceId)?.name
            : (message.senderUser
              ? `${message.senderUser.firstName || ''} ${message.senderUser.lastName || ''}`.trim()
              : groupName)
          notifyIncomingMessage({
            message,
            messageCategoriesById: categoriesForNotify,
            browserNotificationEnabled: profile?.browserNotification,
            senderName: senderName || groupName || 'Message',
            senderDeviceId: message.senderDeviceId,
            receiverChatGroupId: message.receiverChatGroupId
          })
        }

        // Update the open thread list only when chat is subscribed in the UI.
        if (
          !hasOpenThread ||
          !messageShouldUpdateOpenChatList(message, activeContext, profile?.id, devicesById)
        ) {
          continue
        }
        const messageList = yield select(state => state.experiences.chatManagement.messageList)
        const currentIds = Array.isArray(messageList?.data) ? messageList.data : []
        const filteredIds = currentIds.filter(id => !String(id).startsWith('temp-'))
        const nextIds = filteredIds.includes(message.id) ? filteredIds : [message.id, ...filteredIds]
        const total = (messageList?.totalNumberOfItems || 0) + (filteredIds.includes(message.id) ? 0 : 1)
        yield put(ChatManagementExperienceActions.messageList.setData(nextIds, total, false))
        if (activeContext?.deviceId && message.senderDeviceId && message.senderUserId !== profile?.id) {
          const deviceUuidForRead = yield * resolveDeviceUuidForRead(activeContext.deviceId)
          if (deviceUuidForRead) {
            yield spawn(MessageSocket.markThreadRead, deviceUuidForRead)
          }
        }
        if (activeContext?.chatGroupId) {
          yield spawn(MessageSocket.markChatGroupRead, activeContext.chatGroupId)
        }
      }
    }
  })

  // update delivery (received) / read state of operator-sent messages from server receipts
  yield fork(function * watchMessageStatus () {
    const channel = eventChannel(emit => {
      MessageSocket.onMessageStatus(payload => emit(payload))
      return () => {}
    })

    while (true) {
      let payload = yield take(channel)
      // Socket may send array [ "messages read", payload ] or just the payload object
      if (Array.isArray(payload) && payload.length > 1) payload = payload[1]
      const messages = Array.isArray(payload?.messages) ? payload.messages : []
      if (!messages.length) continue
      const isRead = payload?.read === true || payload?.read === 'true'
      const isReceived = isRead || payload?.received === true || payload?.received === 'true'
      if (!isReceived) continue
      const messagesById = getDataStoreEntityMap(yield select(state => state.data.messages))
      const isGroupRead = !!payload?.chatGroupId
      const groupIdForRead = payload?.chatGroupId != null ? String(payload.chatGroupId) : null
      const statusPatch = isRead ? { received: true, read: true } : { received: true }
      for (const entry of messages) {
        const id = entry?.id_message || entry?.id
        // only update messages we already hold to avoid creating partial records
        if (id == null || !messagesById[id]) continue
        if (isGroupRead) {
          const readByAll = entry?.readByAllReceivers === true || entry?.readByAllReceivers === 'true'
          const readByMe = entry?.readByMe === true || entry?.readByMe === 'true'
          yield put(MessageDataStoreSlice.actions.addItem(id, {
            received: readByAll || readByMe,
            read: readByAll
          }))
        } else {
          const existing = messagesById[id]
          const totalRecipients = Number(existing?.totalRecipients) || 0
          if (isRead && totalRecipients >= 2) {
            const readCount = Math.min(
              totalRecipients,
              (Number(existing?.readCount) || 0) + 1
            )
            yield put(MessageDataStoreSlice.actions.addItem(id, {
              received: true,
              readCount,
              read: readCount >= totalRecipients
            }))
          } else {
            yield put(MessageDataStoreSlice.actions.addItem(id, statusPatch))
          }
        }
      }
      if (isGroupRead && groupIdForRead) {
        yield * applyChatGroupUnreadClear(groupIdForRead)
      } else if (isRead && !isGroupRead) {
        const deviceUuidForRead =
          payload?.uuid_device ||
          payload?.uuid_device_target ||
          messages[0]?.uuid_device
        if (deviceUuidForRead) {
          yield * applyMainChatThreadUnreadClear({
            deviceUuid: String(deviceUuidForRead),
            userId: null
          })
        }
      }
      if (isRead) {
        const badge = selectMainChatUnreadBadge(yield select(state => state))
        if (badge.count <= 0) {
          resetTitleFlash()
        }
      }
    }
  })

  yield fork(function * watchChatGroupDeleted () {
    const channel = eventChannel(emit => {
      MessageSocket.onChatGroupDeleted(raw => emit(raw))
      return () => {}
    })
    while (true) {
      let payload = yield take(channel)
      payload = normalizeChatGroupPushPayload(payload)
      const chatGroupId = payload?.id ?? payload?.chatGroupId
      if (!chatGroupId) continue
      yield * removeChatGroupFromExperience(String(chatGroupId))
    }
  })

  yield fork(function * watchChatGroupCreated () {
    const channel = eventChannel(emit => {
      MessageSocket.onChatGroupCreated(raw => emit(raw))
      return () => {}
    })
    while (true) {
      let payload = yield take(channel)
      payload = normalizeChatGroupPushPayload(payload)
      if (!payload?.id) continue
      if (!isChatGroupActive(payload)) continue
      yield * applyChatGroupToStore(payload)
      const record = normalizeChatGroupSocketRecord(payload)
      if (!record?.id) continue
      yield spawn(MessageSocket.subscribeAllChatGroupsForNotifications, [String(record.id)])
      const chatGroupList = yield select(state => state.experiences.chatManagement?.chatGroupList)
      const currentIds = Array.isArray(chatGroupList?.data) ? chatGroupList.data : []
      if (!currentIds.includes(record.id)) {
        yield put(
          ChatManagementExperienceActions.chatGroupList.setData(
            [record.id, ...currentIds],
            (chatGroupList?.totalNumberOfItems || 0) + 1,
            false
          )
        )
      }
    }
  })

  yield fork(function * watchChatGroupUpdated () {
    const channel = eventChannel(emit => {
      MessageSocket.onChatGroupUpdated(raw => emit(raw))
      return () => {}
    })
    while (true) {
      let payload = yield take(channel)
      payload = normalizeChatGroupPushPayload(payload)
      if (!payload?.id) continue
      if (!isChatGroupActive(payload)) {
        yield * removeChatGroupFromExperience(String(payload.id))
        continue
      }
      yield * applyChatGroupToStore(payload)
      const record = normalizeChatGroupSocketRecord(payload)
      if (!record?.id) continue
      const chatGroupList = yield select(state => state.experiences.chatManagement?.chatGroupList)
      const currentIds = Array.isArray(chatGroupList?.data) ? chatGroupList.data : []
      if (currentIds.length > 0 && !currentIds.includes(record.id)) {
        yield put(
          ChatManagementExperienceActions.chatGroupList.setData(
            [record.id, ...currentIds],
            (chatGroupList?.totalNumberOfItems || 0) + 1,
            false
          )
        )
      }
    }
  })

  yield takeLatest(ChatManagementMessageListSlice.types.FETCH, fetchMessages)
  yield takeEvery(CHAT_MANAGEMENT_EXPERIENCE_TYPES.CREATE_MESSAGE, createMessage)
  yield takeLatest(CHAT_MANAGEMENT_EXPERIENCE_TYPES.EDIT_MESSAGE_CATEGORY, editMessageCategory)
  yield takeLatest(ChatManagementChatGroupListSlice.types.FETCH, fetchChatGroups)
  yield takeLatest(CHAT_MANAGEMENT_EXPERIENCE_TYPES.FETCH_CHAT_GROUP_MESSAGES, fetchChatGroupMessages)
  yield takeLatest(CHAT_MANAGEMENT_EXPERIENCE_TYPES.CREATE_CHAT_GROUP, createChatGroup)
  yield takeLatest(CHAT_MANAGEMENT_EXPERIENCE_TYPES.EDIT_CHAT_GROUP, editChatGroup)
  yield takeLatest(CHAT_MANAGEMENT_EXPERIENCE_TYPES.DELETE_CHAT_GROUP, deleteChatGroup)
  yield takeLatest(CHAT_MANAGEMENT_EXPERIENCE_TYPES.PATCH_CHAT_GROUP_PHOTO, patchChatGroupPhoto)
  yield takeLatest(CHAT_MANAGEMENT_EXPERIENCE_TYPES.UPDATE_CHAT_GROUP_MEMBERS, updateChatGroupMembers)
  yield takeEvery(
    CHAT_MANAGEMENT_EXPERIENCE_TYPES.UPDATE_CHAT_GROUP_MEMBER_ASSIGNATION,
    updateChatGroupMemberAssignation
  )
  yield takeLatest(CHAT_MANAGEMENT_EXPERIENCE_TYPES.REFRESH_UNREAD_SOURCES, refreshUnreadSources)
  // subscribe/unsubscribe to socket rooms (guarded so socket errors e.g. Invalid namespace do not cancel the saga)
  yield takeLatest(CHAT_MANAGEMENT_EXPERIENCE_TYPES.MARK_ACTIVE_THREAD_READ, markActiveThreadRead)
  yield takeLatest(LAYOUT_TYPES.TOGGLE_CHAT_DRAWER, onChatDrawerMaybeOpened)
  yield takeLatest(LAYOUT_TYPES.SET_CHAT_DRAWER_OPEN, onChatDrawerMaybeOpened)
  yield takeLatest(CHAT_MANAGEMENT_EXPERIENCE_TYPES.SUBSCRIBE_CONTEXT, function * (action) {
    try {
      const { chatGroupId, userId, deviceId } = action.data
      yield call(MessageSocket.joinChatContext, { chatGroupId, userId, deviceId })
      yield * markActiveThreadRead()
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[ChatManagement] subscribeContext failed:', err?.message || err)
      }
    }
  })
  yield takeLatest(CHAT_MANAGEMENT_EXPERIENCE_TYPES.UNSUBSCRIBE_CONTEXT, function * (action) {
    try {
      const { chatGroupId, userId, deviceId } = action.data
      yield call(MessageSocket.leaveChatContext, { chatGroupId, userId, deviceId })
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[ChatManagement] unsubscribeContext failed:', err?.message || err)
      }
    }
  })
}

export default ChatManagementExperienceSaga
